// Geometrie-Helfer fuer Sektor- und Betriebsgebiet-Polygone.
//
// Lagen urspruenglich in der Sektorverwaltung. Seit dem GeoJSON-Import gibt es
// einen zweiten Aufrufer, und die Selbstueberschneidungs-Pruefung muss dort
// dieselbe sein wie im Karteneditor — sonst laesst der Import ein Polygon
// durch, das die Seite anschliessend als ungueltig meldet.

/// Umschliessendes Rechteck; wird an der Fahrt-Auswertung als Vorfilter vor
/// dem teureren Point-in-Polygon genutzt.
export const calculateBounds = (polygon) => {
  if (!polygon.length) return null;
  return polygon.reduce((bounds, point) => ({
    north: Math.max(bounds.north, point.lat),
    south: Math.min(bounds.south, point.lat),
    east: Math.max(bounds.east, point.lng),
    west: Math.min(bounds.west, point.lng)
  }), {
    north: polygon[0].lat,
    south: polygon[0].lat,
    east: polygon[0].lng,
    west: polygon[0].lng
  });
};

export const calculateCentroid = (polygon) => {
  if (!polygon.length) return null;
  const totals = polygon.reduce((acc, point) => ({
    lat: acc.lat + point.lat,
    lng: acc.lng + point.lng
  }), { lat: 0, lng: 0 });
  return {
    lat: totals.lat / polygon.length,
    lng: totals.lng / polygon.length
  };
};

export const hasSelfIntersection = (polygon) => {
  if (polygon.length < 4) return false;

  const orientation = (a, b, c) => {
    const value = (b.lng - a.lng) * (c.lat - b.lat) - (b.lat - a.lat) * (c.lng - b.lng);
    if (Math.abs(value) < 1e-12) return 0;
    return value > 0 ? 1 : 2;
  };

  const intersects = (p1, q1, p2, q2) => {
    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);
    // Nur echte Kreuzungen zaehlen. Liegt ein Punkt exakt auf der anderen Kante,
    // ist eine der Orientierungen 0 — das ist eine Beruehrung, keine
    // Ueberschneidung. Amtliche Grenzen sind voll davon (Engstellen, doppelte
    // Stuetzpunkte, Zwickel), und die alte Fassung meldete 33 von 39 Kasseler
    // Gemeindegrenzen faelschlich als ungueltig.
    if (o1 === 0 || o2 === 0 || o3 === 0 || o4 === 0) return false;
    return o1 !== o2 && o3 !== o4;
  };

  const edges = polygon.map((point, index) => ({
    start: point,
    end: polygon[(index + 1) % polygon.length],
    index
  }));

  for (let i = 0; i < edges.length; i += 1) {
    for (let j = i + 1; j < edges.length; j += 1) {
      const areAdjacent = Math.abs(edges[i].index - edges[j].index) === 1 ||
        (edges[i].index === 0 && edges[j].index === edges.length - 1);
      if (areAdjacent) continue;
      if (intersects(edges[i].start, edges[i].end, edges[j].start, edges[j].end)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Nimmt lokale Schleifen aus einem Ring heraus.
 *
 * Vereinfachte Umrisse kreuzen sich gelegentlich an genau einer engen Stelle —
 * etwa dort, wo zwei vereinigte Gemeindegrenzen einen schmalen Korridor bilden
 * und Douglas-Peucker die Bahn auf sich selbst faltet. Kreuzen sich zwei nahe
 * beieinander liegende Kanten, wird der eingeschlossene Zipfel entfernt.
 *
 * Bewusst nur bei kleinem Indexabstand: eine Kreuzung weit auseinander
 * liegender Kanten waere eine echte Acht, und die darf nicht stillschweigend
 * halbiert werden.
 */
export const repairSelfIntersections = (polygon, { maxSpan = 12, maxPasses = 20 } = {}) => {
  if (!polygon || polygon.length < 4) return polygon || [];

  const orientation = (a, b, c) => {
    const value = (b.lng - a.lng) * (c.lat - b.lat) - (b.lat - a.lat) * (c.lng - b.lng);
    if (Math.abs(value) < 1e-12) return 0;
    return value > 0 ? 1 : 2;
  };
  const crosses = (p1, q1, p2, q2) => {
    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);
    if (o1 === 0 || o2 === 0 || o3 === 0 || o4 === 0) return false;
    return o1 !== o2 && o3 !== o4;
  };

  let ring = polygon;
  for (let pass = 0; pass < maxPasses; pass += 1) {
    let loop = null;
    for (let i = 0; i < ring.length && !loop; i += 1) {
      const last = Math.min(i + maxSpan, ring.length - 1);
      for (let j = i + 2; j <= last; j += 1) {
        if (i === 0 && j === ring.length - 1) continue;
        if (crosses(ring[i], ring[i + 1], ring[j], ring[(j + 1) % ring.length])) {
          loop = [i, j];
          break;
        }
      }
    }
    if (!loop) break;
    const repaired = [...ring.slice(0, loop[0] + 1), ...ring.slice(loop[1] + 1)];
    if (repaired.length < 3) break;
    ring = repaired;
  }
  return ring;
};
